/**
 * BaseObservability - Abstract base class for Observability implementations
 */

import { MastraBase } from '@mastra/core/base';
import type { RequestContext } from '@mastra/core/di';
import type { IMastraLogger } from '@mastra/core/logger';
import { RegisteredLogger } from '@mastra/core/logger';
import { SpanType, TracingEventType, noOpLoggerContext } from '@mastra/core/observability';
import type {
  Span,
  ObservabilityExporter,
  ObservabilityBridge,
  SpanOutputProcessor,
  TracingEvent,
  AnySpan,
  EndSpanOptions,
  UpdateSpanOptions,
  StartSpanOptions,
  CreateSpanOptions,
  ObservabilityInstance,
  CustomSamplerOptions,
  ExportedSpan,
  AnyExportedSpan,
  TraceState,
  TracingOptions,
  LoggerContext,
  MetricsContext,
  ObservabilityEvent,
  ModelGenerationAttributes,
  UsageStats,
} from '@mastra/core/observability';
import { getNestedValue, setNestedValue } from '@mastra/core/utils';
import { ObservabilityBus } from '../bus';
import type { ObservabilityInstanceConfig } from '../config';
import { SamplingStrategyType } from '../config';
import { LoggerContextImpl } from '../context/logger';
import { MetricsContextImpl } from '../context/metrics';
import { emitAutoExtractedMetrics, emitTokenMetricsForUsage } from '../metrics/auto-extract';
import { CardinalityFilter } from '../metrics/cardinality';
import { NoOpSpan } from '../spans';
import { addUsageStats } from '../usage';

// ============================================================================
// Abstract Base Class
// ============================================================================

/**
 * Abstract base class for all Observability implementations in Mastra.
 */
export abstract class BaseObservabilityInstance extends MastraBase implements ObservabilityInstance {
  protected config: ObservabilityInstanceConfig;

  /**
   * Unified event bus for all observability signals.
   * Routes events to registered exporters based on event type.
   */
  protected observabilityBus: ObservabilityBus;

  /**
   * Cardinality filter for metrics label protection.
   */
  protected cardinalityFilter: CardinalityFilter;

  /**
   * Deployment environment propagated from the parent Mastra instance.
   * Set by `Observability.setMastraContext`, read by spans as a fallback when
   * a span's `metadata.environment` isn't set.
   */
  #mastraEnvironment?: string;

  constructor(config: ObservabilityInstanceConfig) {
    super({ component: RegisteredLogger.OBSERVABILITY, name: config.serviceName });

    // Apply defaults for optional fields
    this.config = {
      serviceName: config.serviceName,
      name: config.name,
      sampling: config.sampling ?? { type: SamplingStrategyType.ALWAYS },
      exporters: config.exporters ?? [],
      spanOutputProcessors: config.spanOutputProcessors ?? [],
      bridge: config.bridge ?? undefined,
      includeInternalSpans: config.includeInternalSpans ?? false,
      excludeSpanTypes: config.excludeSpanTypes,
      spanFilter: config.spanFilter,
      requestContextKeys: config.requestContextKeys ?? [],
      serializationOptions: config.serializationOptions,
      logging: config.logging,
    };

    // Initialize cardinality filter for metrics (uses user config or defaults)
    this.cardinalityFilter = new CardinalityFilter(config.cardinality);

    // Initialize the unified ObservabilityBus
    this.observabilityBus = new ObservabilityBus({
      serializationOptions: this.config.serializationOptions,
    });

    for (const exporter of this.exporters) {
      this.observabilityBus.registerExporter(exporter);
    }

    // Register bridge on the bus so it receives all signals (tracing + non-tracing)
    if (this.config.bridge) {
      this.observabilityBus.registerBridge(this.config.bridge);
    }

    // Initialize bridge if present
    if (this.config.bridge?.init) {
      this.config.bridge.init({ config: this.config });
    }
  }

  /**
   * Override setLogger to add Observability specific initialization log
   * and propagate logger to exporters and bridge
   */
  __setLogger(logger: IMastraLogger) {
    super.__setLogger(logger);

    // Propagate logger to all exporters that support it
    this.exporters.forEach(exporter => {
      if (typeof exporter.__setLogger === 'function') {
        exporter.__setLogger(logger);
      }
    });

    // Propagate logger to bridge if present
    if (this.config.bridge?.__setLogger) {
      this.config.bridge.__setLogger(logger);
    }

    // Log Observability initialization details after logger is properly set
    this.logger.debug(
      `[Observability] Initialized [service=${this.config.serviceName}] [instance=${this.config.name}] [sampling=${this.config.sampling?.type}] [bridge=${!!this.config.bridge}]`,
    );
  }

  // ============================================================================
  // Protected getters for clean config access
  // ============================================================================

  protected get exporters(): ObservabilityExporter[] {
    return this.config.exporters || [];
  }

  protected get spanOutputProcessors(): SpanOutputProcessor[] {
    return this.config.spanOutputProcessors || [];
  }

  // ============================================================================
  // Public API - Single type-safe span creation method
  // ============================================================================

  /**
   * Start a new span of a specific SpanType
   *
   * Sampling Decision:
   * - For root spans (no parent): Perform sampling check using the configured strategy
   * - For child spans: Inherit the sampling decision from the parent
   *   - If parent is a NoOpSpan (not sampled), child is also a NoOpSpan
   *   - If parent is a valid span (sampled), child is also sampled
   *
   * This ensures trace-level sampling: either all spans in a trace are sampled or none are.
   * See: https://github.com/mastra-ai/mastra/issues/11504
   */
  startSpan<TType extends SpanType>(options: StartSpanOptions<TType>): Span<TType> {
    const { customSamplerOptions, requestContext, metadata, tracingOptions, ...rest } = options;

    // Determine sampling: inherit from parent or make new decision for root spans
    if (options.parent) {
      // Child span: inherit sampling decision from parent
      // If parent is a NoOpSpan (not sampled), child should also be a NoOpSpan
      if (!options.parent.isValid) {
        return new NoOpSpan<TType>({ ...rest, metadata }, this);
      }
      // Parent is valid (sampled), so child will also be sampled - continue to create actual span
    } else {
      // Root span: perform sampling check
      if (!this.shouldSample(customSamplerOptions)) {
        return new NoOpSpan<TType>({ ...rest, metadata }, this);
      }
    }

    // Compute or inherit TraceState
    let traceState: TraceState | undefined;

    if (options.parent) {
      // Child span: inherit from parent
      traceState = options.parent.traceState;
    } else {
      // Root span: compute new TraceState
      traceState = this.computeTraceState(tracingOptions);
    }

    // Merge tracingOptions.metadata with span metadata (tracingOptions.metadata takes precedence for root spans)
    const tracingMetadata = !options.parent ? tracingOptions?.metadata : undefined;
    const mergedMetadata = metadata || tracingMetadata ? { ...metadata, ...tracingMetadata } : undefined;

    // Extract metadata from RequestContext
    const enrichedMetadata = this.extractMetadataFromRequestContext(requestContext, mergedMetadata, traceState);

    // Inject the Mastra-level environment into root-span metadata when nothing
    // upstream provided one. Root-only is sufficient because BaseSpan inherits
    // parent metadata, so descendants pick the value up automatically.
    // Persisting it on metadata (rather than only computing it in
    // getCorrelationContext) is what lets the storage record-builders populate
    // the `environment` column on SpanRecord, which is then read by stored
    // score/feedback events via RecordedSpan / RecordedTrace.addScore.
    const finalMetadata =
      !options.parent &&
      this.#mastraEnvironment !== undefined &&
      (enrichedMetadata === undefined || enrichedMetadata.environment === undefined)
        ? { ...(enrichedMetadata ?? {}), environment: this.#mastraEnvironment }
        : enrichedMetadata;

    // Tags are only passed for root spans (no parent)
    const tags = !options.parent ? tracingOptions?.tags : undefined;

    // Extract traceId and parentSpanId from tracingOptions for root spans (no parent)
    // These allow nested workflows to join the parent workflow's trace
    const traceId = !options.parent ? (options.traceId ?? tracingOptions?.traceId) : options.traceId;
    const parentSpanId = !options.parent
      ? (options.parentSpanId ?? tracingOptions?.parentSpanId)
      : options.parentSpanId;

    const span = this.createSpan<TType>({
      ...rest,
      traceId,
      parentSpanId,
      metadata: finalMetadata,
      traceState,
      tags,
      requestContext,
    });

    if (span.isEvent) {
      this.emitSpanEnded(span);
    } else {
      // Automatically wire up tracing lifecycle
      this.wireSpanLifecycle(span);

      // Emit span started event
      this.emitSpanStarted(span);
    }

    return span;
  }

  /**
   * Rebuild a span from exported data for lifecycle operations.
   * Used by durable execution engines (e.g., Inngest) to end/update spans
   * that were created in a previous durable operation.
   *
   * The rebuilt span:
   * - Does NOT emit SPAN_STARTED (assumes original span already did)
   * - Can have end(), update(), error() called on it
   * - Will emit SPAN_ENDED or SPAN_UPDATED when those methods are called
   *
   * @param cached - The exported span data to rebuild from
   * @returns A span that can have lifecycle methods called on it
   */
  rebuildSpan<TType extends SpanType>(cached: ExportedSpan<TType>): Span<TType> {
    // Create span with existing IDs from cached data
    const span = this.createSpan<TType>({
      name: cached.name,
      type: cached.type,
      traceId: cached.traceId,
      spanId: cached.id,
      parentSpanId: cached.parentSpanId,
      startTime: cached.startTime instanceof Date ? cached.startTime : new Date(cached.startTime),
      input: cached.input,
      attributes: cached.attributes,
      metadata: cached.metadata,
      entityType: cached.entityType,
      entityId: cached.entityId,
      entityName: cached.entityName,
    });

    // Wire up lifecycle events (but skip SPAN_STARTED since it was already emitted)
    this.wireSpanLifecycle(span);

    return span;
  }

  // ============================================================================
  // Abstract Methods - Must be implemented by concrete classes
  // ============================================================================

  /**
   * Create a new span (called after sampling)
   *
   * Implementations should:
   * 1. Create a plain span with the provided attributes
   * 2. Return the span - base class handles all tracing lifecycle automatically
   *
   * The base class will automatically:
   * - Set trace relationships
   * - Wire span lifecycle callbacks
   * - Emit span_started event
   */
  protected abstract createSpan<TType extends SpanType>(options: CreateSpanOptions<TType>): Span<TType>;

  // ============================================================================
  // Configuration Management
  // ============================================================================

  /**
   * Get current configuration
   */
  getConfig(): Readonly<ObservabilityInstanceConfig> {
    return { ...this.config };
  }

  /**
   * Returns the deployment environment propagated from the parent Mastra instance.
   * Spans use this as a fallback when `metadata.environment` isn't set.
   */
  getMastraEnvironment(): string | undefined {
    return this.#mastraEnvironment;
  }

  /**
   * Internal hook used by `Observability.setMastraContext` to push the
   * resolved Mastra-level environment into this instance.
   */
  __setMastraEnvironment(environment: string | undefined): void {
    this.#mastraEnvironment = environment;
  }

  // ============================================================================
  // Plugin Access
  // ============================================================================

  /**
   * Get all exporters
   */
  getExporters(): readonly ObservabilityExporter[] {
    return [...this.exporters];
  }

  /**
   * Register an additional exporter at runtime.
   * Adds to both the bus (for event routing) and the config (for getExporters).
   */
  registerExporter(exporter: ObservabilityExporter): void {
    this.observabilityBus.registerExporter(exporter);
    this.config.exporters ??= [];
    if (this.config.exporters.includes(exporter)) {
      return;
    }
    this.config.exporters.push(exporter);

    if (typeof exporter.__setLogger === 'function') {
      exporter.__setLogger(this.logger);
    }
  }

  /**
   * Get all span output processors
   */
  getSpanOutputProcessors(): readonly SpanOutputProcessor[] {
    return [...this.spanOutputProcessors];
  }

  /**
   * Get the bridge instance if configured
   */
  getBridge(): ObservabilityBridge | undefined {
    return this.config.bridge;
  }

  /**
   * Get the logger instance (for exporters and other components)
   */
  getLogger() {
    return this.logger;
  }

  /**
   * Get the ObservabilityBus for this instance.
   * The bus routes all observability events (tracing, logs, metrics, scores, feedback)
   * to registered exporters based on event type.
   */
  getObservabilityBus(): ObservabilityBus {
    return this.observabilityBus;
  }

  // ============================================================================
  // Context-factory bridge methods
  // ============================================================================

  /**
   * Get a LoggerContext correlated to a span.
   * Called by the context-factory in core (deriveLoggerContext) so that
   * `observabilityContext.loggerVNext` is a real logger instead of no-op.
   */
  getLoggerContext(span?: AnySpan): LoggerContext {
    if (this.config.logging?.enabled === false) {
      return noOpLoggerContext;
    }

    const correlationContext = span?.getCorrelationContext?.();
    const metadata: Record<string, unknown> | undefined = span?.metadata ? structuredClone(span.metadata) : undefined;

    return new LoggerContextImpl({
      traceId: span?.traceId,
      spanId: span?.id,
      correlationContext,
      metadata,
      observabilityBus: this.observabilityBus,
      minLevel: this.config.logging?.level,
    });
  }

  /**
   * Get a MetricsContext correlated to a span.
   * Called by the context-factory in core (deriveMetricsContext) so that
   * `observabilityContext.metrics` is a real metrics context instead of no-op.
   */
  getMetricsContext(span?: AnySpan): MetricsContext {
    const correlationContext = span?.getCorrelationContext?.();
    const metadata: Record<string, unknown> | undefined = span?.metadata ? structuredClone(span.metadata) : undefined;

    return new MetricsContextImpl({
      traceId: span?.traceId,
      spanId: span?.id,
      correlationContext,
      metadata,
      cardinalityFilter: this.cardinalityFilter,
      observabilityBus: this.observabilityBus,
    });
  }

  /**
   * Emit any observability event through the bus.
   * The bus routes the event to the appropriate handler on each registered exporter,
   * and for tracing events triggers auto-extracted metrics.
   */
  protected emitObservabilityEvent(event: ObservabilityEvent): void {
    this.observabilityBus.emit(event);
  }

  /**
   * Internal hook used by RecordedTrace/RecordedSpan hydration to route
   * non-tracing annotation events back through the normal exporter pipeline.
   */
  __emitRecordedEvent(event: ObservabilityEvent): void {
    this.emitObservabilityEvent(event);
  }

  /**
   * Internal hook used by the client observability proxy (`client/`)
   * to route already-validated events through the normal bus without
   * going through the live span lifecycle. The caller is responsible
   * for constructing well-formed `ExportedSpan`s/`ExportedLog`s and
   * for any validation needed.
   */
  __receiveExternalEvent(event: ObservabilityEvent): void {
    this.emitObservabilityEvent(event);
  }

  // ============================================================================
  // Span Lifecycle Management
  // ============================================================================

  /**
   * Automatically wires up Observability lifecycle events for any span
   * This ensures all spans emit events regardless of implementation
   */
  private wireSpanLifecycle<TType extends SpanType>(span: Span<TType>): void {
    // Skip wiring for filtered internal spans, except MODEL_GENERATION —
    // those need the wrap so captureModelUsageRollup can intercept usage
    // before originalEnd discards it. Other internal types (AGENT_RUN,
    // WORKFLOW_RUN, MODEL_STEP, MODEL_CHUNK, …) carry nothing to roll up,
    // and skipping the closure-per-span cost matters in streaming hot
    // paths like per-chunk MODEL_CHUNK spans.
    if (!this.config.includeInternalSpans && span.isInternal && span.type !== SpanType.MODEL_GENERATION) {
      return;
    }

    // Store original methods
    const originalEnd = span.end.bind(span);
    const originalUpdate = span.update.bind(span);

    // Wrap methods to automatically emit tracing events
    span.end = (options?: EndSpanOptions<TType>) => {
      if (span.isEvent) {
        this.logger.warn(`End event is not available on event spans`);
        return;
      }

      // Capture rollup usage BEFORE originalEnd runs: excluded spans
      // drop end-time attributes (see DefaultSpan#end), so the only
      // place to read MODEL_GENERATION usage for a filtered span is the
      // end() options being passed in right now.
      const rollupTarget = this.captureModelUsageRollup(span, options);

      originalEnd(options);

      if (rollupTarget) {
        this.applyUsageRollup(rollupTarget);
      }

      this.emitSpanEnded(span);
    };

    span.update = (options: UpdateSpanOptions<TType>) => {
      if (span.isEvent) {
        this.logger.warn(`Update() is not available on event spans`);
        return;
      }
      originalUpdate(options);
      this.emitSpanUpdated(span);
    };
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Check if a trace should be sampled
   */
  protected shouldSample(options?: CustomSamplerOptions): boolean {
    // Check built-in sampling strategy
    const { sampling } = this.config;

    switch (sampling?.type) {
      case undefined:
        return true;
      case SamplingStrategyType.ALWAYS:
        return true;
      case SamplingStrategyType.NEVER:
        return false;
      case SamplingStrategyType.RATIO:
        if (sampling.probability === undefined || sampling.probability < 0 || sampling.probability > 1) {
          this.logger.warn(
            `Invalid sampling probability: ${sampling.probability}. Expected value between 0 and 1. Defaulting to no sampling.`,
          );
          return false;
        }
        return Math.random() < sampling.probability;
      case SamplingStrategyType.CUSTOM:
        return sampling.sampler(options);
      default:
        throw new Error(`Sampling strategy type not implemented: ${(sampling as any).type}`);
    }
  }

  /**
   * Compute TraceState for a new trace based on configured and per-request keys
   */
  protected computeTraceState(tracingOptions?: TracingOptions): TraceState | undefined {
    const configuredKeys = this.config.requestContextKeys ?? [];
    const additionalKeys = tracingOptions?.requestContextKeys ?? [];

    // Merge: configured + additional
    const allKeys = [...configuredKeys, ...additionalKeys];

    const hideInput = tracingOptions?.hideInput;
    const hideOutput = tracingOptions?.hideOutput;

    // Return undefined if no TraceState properties are needed
    if (allKeys.length === 0 && !hideInput && !hideOutput) {
      return undefined;
    }

    return {
      requestContextKeys: allKeys,
      ...(hideInput !== undefined && { hideInput }),
      ...(hideOutput !== undefined && { hideOutput }),
    };
  }

  /**
   * Extract metadata from RequestContext using TraceState
   */
  protected extractMetadataFromRequestContext(
    requestContext: RequestContext | undefined,
    explicitMetadata: Record<string, any> | undefined,
    traceState: TraceState | undefined,
  ): Record<string, any> | undefined {
    if (!requestContext || !traceState || traceState.requestContextKeys.length === 0) {
      return explicitMetadata;
    }

    const extracted = this.extractKeys(requestContext, traceState.requestContextKeys);

    // Only return an object if we have extracted or explicit metadata
    if (Object.keys(extracted).length === 0 && !explicitMetadata) {
      return undefined;
    }

    return {
      ...extracted,
      ...explicitMetadata, // Explicit metadata always wins
    };
  }

  /**
   * Extract specific keys from RequestContext
   */
  protected extractKeys(requestContext: RequestContext, keys: string[]): Record<string, any> {
    const result: Record<string, any> = {};

    for (const key of keys) {
      // Handle dot notation: get first part from RequestContext, then navigate nested properties
      const parts = key.split('.');
      const rootKey = parts[0]!; // parts[0] always exists since key is a non-empty string
      const value = requestContext.get(rootKey);

      if (value !== undefined) {
        // If there are nested parts, extract them from the value
        if (parts.length > 1) {
          const nestedPath = parts.slice(1).join('.');
          const nestedValue = getNestedValue(value, nestedPath);
          if (nestedValue !== undefined) {
            setNestedValue(result, key, nestedValue);
          }
        } else {
          // Simple key, set directly
          setNestedValue(result, key, value);
        }
      }
    }

    return result;
  }

  /**
   * Process a span through all output processors
   */
  private processSpan(span?: AnySpan): AnySpan | undefined {
    for (const processor of this.spanOutputProcessors) {
      if (!span) {
        break;
      }

      try {
        span = processor.process(span);
      } catch (error) {
        this.logger.error(`[Observability] Processor error [name=${processor.name}]`, error);
        // Continue with other processors
      }
    }

    return span;
  }

  // ============================================================================
  // Event-driven Export Methods
  // ============================================================================

  /** Process a span through output processors and export it, returning undefined if filtered out. */
  getSpanForExport(span: AnySpan): AnyExportedSpan | undefined {
    if (!span.isValid) return undefined;
    if (span.isInternal && !this.config.includeInternalSpans) return undefined;

    // Check excludeSpanTypes before processing
    if (this.config.excludeSpanTypes?.includes(span.type)) return undefined;

    const processedSpan = this.processSpan(span);
    const exportedSpan = processedSpan?.exportSpan(this.config.includeInternalSpans);
    if (!exportedSpan) return undefined;

    // Apply spanFilter on the exported span data
    if (this.config.spanFilter) {
      try {
        if (!this.config.spanFilter(exportedSpan)) return undefined;
      } catch (error) {
        this.logger.error(`[Observability] spanFilter error`, error);
        // On filter error, keep the span to avoid silent data loss
      }
    }

    return exportedSpan;
  }

  /**
   * Emit a span started event.
   * Routes through the ObservabilityBus so exporters receive it via onTracingEvent.
   */
  protected emitSpanStarted(span: AnySpan): void {
    const exportedSpan = this.getSpanForExport(span);
    if (exportedSpan) {
      const event: TracingEvent = { type: TracingEventType.SPAN_STARTED, exportedSpan };
      this.emitTracingEvent(event);
    }
  }

  /**
   * Emit a span ended event (called automatically when spans end).
   * Emits any auto-extracted metrics while the live span tree is still available,
   * then routes the exported tracing event through the ObservabilityBus.
   */
  protected emitSpanEnded(span: AnySpan): void {
    const exportedSpan = this.getSpanForExport(span);

    if (exportedSpan) {
      try {
        // TODO: We intentionally export first so auto-extracted metrics are skipped
        // when the span is filtered out by processors. Metrics still use the live
        // span for correlation and parent traversal, but current span processors
        // mutate spans in place during export, so those mutations can still affect
        // the live span before metrics run. Future options to explore:
        // 1. Make span processors pure/non-mutating.
        // 2. Split trace processors from metric-specific processors/enrichers.
        // 3. Revisit whether auto-extracted metrics should run before export.
        emitAutoExtractedMetrics(span, this.getMetricsContext(span));
      } catch (err) {
        this.logger.error('[Observability] Auto-extraction error:', err);
      }

      const event: TracingEvent = { type: TracingEventType.SPAN_ENDED, exportedSpan };
      this.emitTracingEvent(event);
    }
  }

  /**
   * Emit a span updated event.
   * Routes through the ObservabilityBus so exporters receive it via onTracingEvent.
   */
  protected emitSpanUpdated(span: AnySpan): void {
    const exportedSpan = this.getSpanForExport(span);
    if (exportedSpan) {
      const event: TracingEvent = { type: TracingEventType.SPAN_UPDATED, exportedSpan };
      this.emitTracingEvent(event);
    }
  }

  /**
   * When an internal MODEL_GENERATION span ends, capture the rollup payload
   * (usage, provider, model, target ancestor) needed to attribute its cost
   * to the closest exported ancestor span. Returns undefined when no rollup
   * applies — non-MODEL_GENERATION spans, spans that will be exported, or
   * spans whose usage isn't available at end time.
   */
  private captureModelUsageRollup<TType extends SpanType>(
    span: Span<TType>,
    endOptions: EndSpanOptions<TType> | undefined,
  ): { ancestor: AnySpan; usage: UsageStats; provider?: string; model?: string } | undefined {
    if (span.type !== SpanType.MODEL_GENERATION) return undefined;
    // If the span itself will be exported, the existing auto-extract pipeline
    // emits its metrics; nothing to roll up.
    if (!span.isInternal || this.config.includeInternalSpans) return undefined;

    // For excluded spans, end() options carry the only copy of attributes —
    // the live span discards them in DefaultSpan#end. The liveAttrs fallback
    // is dead for the default implementation but kept for non-DefaultSpan
    // Span implementations that might preserve attributes on excluded spans.
    const endAttrs = (endOptions?.attributes as ModelGenerationAttributes | undefined) ?? undefined;
    const liveAttrs = span.attributes as ModelGenerationAttributes | undefined;
    const usage = endAttrs?.usage ?? liveAttrs?.usage;
    if (!usage) return undefined;

    const ancestor = this.findExportedAncestor(span);
    if (!ancestor) return undefined;

    const provider = endAttrs?.provider ?? liveAttrs?.provider;
    const model = endAttrs?.responseModel ?? endAttrs?.model ?? liveAttrs?.responseModel ?? liveAttrs?.model;

    return { ancestor, usage, provider, model };
  }

  /**
   * Accumulate usage onto the ancestor's `internalUsage` attribute (for trace
   * UI visibility) and emit auto-extracted token metrics now, using the
   * ancestor's metrics context so cost / token labels point at the visible
   * span instead of the hidden agent that incurred them.
   */
  private applyUsageRollup(target: { ancestor: AnySpan; usage: UsageStats; provider?: string; model?: string }): void {
    const { ancestor, usage, provider, model } = target;

    // Mutate the live ancestor's attributes directly. BaseSpan's constructor
    // guarantees `attributes` is always at least `{}` (see spans/base.ts),
    // and the ancestor hasn't ended yet (we're inside a descendant's end()),
    // so the export will pick up the mutated field.
    const attrs = ancestor.attributes as { internalUsage?: UsageStats };
    attrs.internalUsage = addUsageStats(attrs.internalUsage, usage);

    try {
      emitTokenMetricsForUsage(usage, provider, model, this.getMetricsContext(ancestor));
    } catch (err) {
      this.logger.error('[Observability] Usage rollup metric emission error:', err);
    }
  }

  /**
   * Walk up the parent chain to find the closest ancestor that will actually
   * reach exporters. Skips both internal-filtered ancestors and ancestors
   * whose type matches `excludeSpanTypes`, so the rollup target is one whose
   * mutated `internalUsage` attribute is visible in exported traces.
   *
   * Note: this does not preemptively run `spanFilter` — that filter can be
   * async and have side effects, so the rare case of a `spanFilter`-dropped
   * ancestor falls through.
   */
  private findExportedAncestor(span: AnySpan): AnySpan | undefined {
    let ancestor: AnySpan | undefined = span.parent;
    while (ancestor && this.isFilteredFromExport(ancestor)) {
      ancestor = ancestor.parent;
    }
    return ancestor;
  }

  /**
   * Returns true when a span would be dropped by `getSpanForExport` for a
   * reason cheap to check up-front (internal-span filtering or
   * `excludeSpanTypes`). Used by `findExportedAncestor` to skip rollup
   * targets that would silently lose their `internalUsage` attribute.
   */
  private isFilteredFromExport(span: AnySpan): boolean {
    if (span.isInternal && !this.config.includeInternalSpans) return true;
    if (this.config.excludeSpanTypes?.includes(span.type)) return true;
    return false;
  }

  /**
   * Emit a tracing event through the bus.
   *
   * The bus routes the event to each registered exporter's and bridge's
   * onTracingEvent handler.
   */
  private emitTracingEvent(event: TracingEvent): void {
    this.observabilityBus.emit(event);
  }

  /**
   * Export tracing event through all exporters and bridge.
   *
   * @deprecated Prefer emitTracingEvent() which routes through the bus.
   * Kept for backward compatibility with subclasses that may override it.
   */
  protected async exportTracingEvent(event: TracingEvent): Promise<void> {
    // Collect all export targets
    const targets: Array<{ name: string; exportTracingEvent: (event: TracingEvent) => Promise<void> }> = [
      ...this.exporters,
    ];

    // Add bridge if present
    if (this.config.bridge) {
      targets.push(this.config.bridge);
    }

    // Export to all targets
    const exportPromises = targets.map(async target => {
      try {
        await target.exportTracingEvent(event);
        this.logger.debug(`[Observability] Event exported [target=${target.name}] [type=${event.type}]`);
      } catch (error) {
        this.logger.error(`[Observability] Export error [target=${target.name}]`, error);
        // Don't rethrow - continue with other targets
      }
    });

    await Promise.allSettled(exportPromises);
  }

  // ============================================================================
  // Lifecycle Management
  // ============================================================================

  /**
   * Initialize Observability (called by Mastra during component registration)
   */
  init(): void {
    this.logger.debug(`[Observability] Initialization started [name=${this.name}]`);

    // Any initialization logic for the Observability system
    // This could include setting up queues, starting background processes, etc.

    this.logger.info(`[Observability] Initialized successfully [name=${this.name}]`);
  }

  /**
   * Flush all observability data: awaits in-flight handler promises, then
   * drains exporter and bridge SDK-internal buffers.
   *
   * Delegates to ObservabilityBus.flush() which owns the two-phase logic.
   *
   * This is critical for durable execution engines (e.g., Inngest) where
   * the process may be interrupted after a step completes. Calling flush()
   * outside the durable step ensures all span data reaches external systems.
   */
  async flush(): Promise<void> {
    this.logger.debug(`[Observability] Flush started [name=${this.name}]`);
    await this.observabilityBus.flush();
    this.logger.debug(`[Observability] Flush completed [name=${this.name}]`);
  }

  /**
   * Shutdown Observability and clean up resources
   */
  async shutdown(): Promise<void> {
    this.logger.debug(`[Observability] Shutdown started [name=${this.name}]`);

    // Phase 1: Shutdown the ObservabilityBus first (flushes remaining events, clears subscribers)
    await this.observabilityBus.shutdown();

    // Phase 2: Shutdown exporters, processors, and bridge after bus has flushed
    const shutdownPromises: Promise<void>[] = [
      ...this.exporters.map(e => e.shutdown()),
      ...this.spanOutputProcessors.map(p => p.shutdown()),
    ];
    if (this.config.bridge) {
      shutdownPromises.push(this.config.bridge.shutdown());
    }
    if (shutdownPromises.length > 0) {
      const results = await Promise.allSettled(shutdownPromises);
      for (const result of results) {
        if (result.status === 'rejected') {
          this.logger.error(`[Observability] Component shutdown failed [name=${this.name}]:`, result.reason);
        }
      }
    }

    this.logger.info(`[Observability] Shutdown completed [name=${this.name}]`);
  }
}
